import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Mail, MessageCircle, ChevronLeft } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import JsonLD, { buildBreadcrumb } from "../components/JsonLD";
import { useLanguage } from "../context/LanguageContext";
import { SITE_NAME, SITE_URL, CONTACT_EMAIL } from "../translations";

const content = {
  en: {
    title: "Contact FileNesto",
    subtitle: "Have a question, suggestion, or partnership idea? We'd love to hear from you.",
    description: "Contact the FileNesto team — email us at contact@filenesto.com for questions, feedback, partnerships, or feature requests.",
    intro: "We typically reply within 1–2 business days. For the fastest response, please include relevant details (browser, the tool used, a screenshot if applicable).",
    emailLabel: "Email us",
    emailNote: "Click the email below to open your mail client.",
    topics: "What can we help with?",
    topicsList: [
      "Bug reports or unexpected behaviour",
      "Feature requests for new file tools",
      "Partnerships, integrations, or API access",
      "Press, content, or affiliate inquiries",
      "AdSense or advertising questions",
    ],
    notice: "Please do not send confidential or sensitive files to our contact email. FileNesto processes files only through the secure tools on this site.",
  },
  fr: {
    title: "Contacter FileNesto",
    subtitle: "Une question, une suggestion ou une idée de partenariat ? Nous serions ravis d'avoir de vos nouvelles.",
    description: "Contactez l'équipe FileNesto — écrivez-nous à contact@filenesto.com pour vos questions, retours, partenariats ou demandes de fonctionnalités.",
    intro: "Nous répondons généralement sous 1 à 2 jours ouvrés. Pour une réponse rapide, merci d'inclure les détails utiles (navigateur, outil utilisé, capture d'écran si applicable).",
    emailLabel: "Écrivez-nous",
    emailNote: "Cliquez sur l'email ci-dessous pour ouvrir votre client mail.",
    topics: "En quoi pouvons-nous aider ?",
    topicsList: [
      "Signalement de bug ou comportement inattendu",
      "Demandes de nouvelles fonctionnalités",
      "Partenariats, intégrations ou accès API",
      "Demandes presse, contenu ou affiliation",
      "Questions AdSense ou publicitaires",
    ],
    notice: "Merci de ne pas envoyer de fichiers confidentiels ou sensibles à notre email de contact. FileNesto traite les fichiers uniquement via les outils sécurisés du site.",
  },
};

export default function Contact() {
  const { lang, t } = useLanguage();
  const location = useLocation();
  const c = content[lang] || content.en;

  useEffect(() => {
    document.title = c.title.includes(SITE_NAME) ? c.title : `${c.title} — ${SITE_NAME}`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", c.description);
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.rel = "canonical"; document.head.appendChild(link); }
    link.href = `${SITE_URL}${location.pathname}`;
  }, [c, location.pathname]);

  return (
    <div className="min-h-screen bg-white page-fade-in" data-testid="page-contact">
      <JsonLD data={buildBreadcrumb(c.title, location.pathname)} />
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <div className="mb-6">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-blue-600 transition-colors">
            <ChevronLeft className="w-4 h-4" />{t.nav.home}
          </Link>
        </div>
        <header className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {c.title}
          </h1>
          <p className="text-slate-500 text-base">{c.subtitle}</p>
        </header>

        <p className="text-slate-600 leading-relaxed mb-8">{c.intro}</p>

        <div className="rounded-2xl border border-slate-200 bg-slate-50/50 p-6 mb-8" data-testid="contact-email-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <Mail className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="font-semibold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{c.emailLabel}</h2>
              <p className="text-xs text-slate-500">{c.emailNote}</p>
            </div>
          </div>
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            data-testid="contact-email-link"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-blue-600 text-white font-semibold text-base hover:bg-blue-700 active:scale-95 transition-all"
          >
            <Mail className="w-4 h-4" />
            {CONTACT_EMAIL}
          </a>
        </div>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-slate-900 mb-3 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
            <MessageCircle className="w-5 h-5 text-blue-600" />
            {c.topics}
          </h2>
          <ul className="list-disc list-inside text-slate-600 space-y-1">
            {c.topicsList.map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        </section>

        <div className="text-xs text-slate-500 p-4 rounded-xl bg-amber-50 border border-amber-100" data-testid="contact-notice">
          ⚠️ {c.notice}
        </div>
      </main>
      <Footer />
    </div>
  );
}
